import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaExternalLinkAlt,
} from "react-icons/fa";

import {
  JUDO_PRESENCE_PRO,
  JUDO_PRESENCE_TECHNICAL_POINTS,
} from "../data/judoPresenceProData";

import { cn } from "../utils/judoPresenceProUtils";

export default function JudoPresenceProHero() {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveImageIndex((currentIndex) => {
        return (
          (currentIndex + 1) %
          JUDO_PRESENCE_PRO.heroImages.length
        );
      });
    }, 6500);

    return () => window.clearInterval(intervalId);
  }, []);

  const scrollToFeatures = () => {
    document.getElementById("presence-features")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <section className="relative overflow-hidden bg-[#071a35] pt-[76px] text-white">
      <div className="pointer-events-none absolute inset-0 opacity-[0.14]">
        <div className="absolute -right-24 top-10 h-[430px] w-[430px] rounded-full border-[80px] border-[#0868f2]" />
        <div className="absolute -bottom-40 left-[26%] h-[380px] w-[380px] rounded-full border-[70px] border-[#ff7900]" />
      </div>

      <div className="relative mx-auto max-w-[1480px] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-[0.88fr_1.12fr] lg:items-center xl:gap-16">
          <div>
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center rounded-[20px] border border-white/10 bg-white p-2 shadow-xl">
                <img
                  src={JUDO_PRESENCE_PRO.logo}
                  alt="Logo Judo Présence Pro"
                  className="h-full w-full object-contain"
                />
              </div>

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-blue-200">
                  {JUDO_PRESENCE_PRO.eyebrow}
                </p>

                <p className="mt-1 text-lg font-semibold">
                  {JUDO_PRESENCE_PRO.name}
                </p>
              </div>
            </div>

            <h1 className="mt-8 max-w-3xl text-4xl font-semibold leading-[1.04] tracking-[-0.045em] sm:text-5xl lg:text-6xl xl:text-7xl">
              {JUDO_PRESENCE_PRO.title}

              <span className="block text-[#61a2ff]">
                {JUDO_PRESENCE_PRO.highlightedTitle}
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
              {JUDO_PRESENCE_PRO.description}
            </p>

            <div className="mt-8 space-y-3">
              {JUDO_PRESENCE_TECHNICAL_POINTS.map((point) => {
                const Icon = point.icon;

                return (
                  <div
                    key={point.label}
                    className="flex items-center gap-3 text-sm text-white/85"
                  >
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-white/10 text-[#ff7900]">
                      <Icon className="text-[10px]" />
                    </span>

                    <span>{point.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href={JUDO_PRESENCE_PRO.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex h-12 items-center justify-center gap-3 rounded-full bg-white px-6 text-sm font-semibold text-[#071a35] transition hover:bg-blue-50"
              >
                Accéder à l’application

                <span className="grid h-7 w-7 place-items-center rounded-full bg-[#ff7900] text-white">
                  <FaExternalLinkAlt className="text-[9px]" />
                </span>
              </a>

              <button
                type="button"
                onClick={scrollToFeatures}
                className="group inline-flex h-12 items-center justify-center gap-3 rounded-full border border-white/25 bg-white/5 px-6 text-sm font-semibold text-white backdrop-blur-sm transition hover:border-white/50 hover:bg-white/10"
              >
                Découvrir les fonctionnalités

                <FaArrowRight className="text-[10px] transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>

          <div>
            <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-white/5 p-2 shadow-[0_35px_100px_-45px_rgba(0,0,0,0.8)]">
              <div className="relative aspect-[16/11] overflow-hidden rounded-[18px] bg-slate-950">
                {JUDO_PRESENCE_PRO.heroImages.map(
                  (image, index) => (
                    <img
                      key={image.src}
                      src={image.src}
                      alt={image.alt}
                      className={cn(
                        "absolute inset-0 h-full w-full object-cover transition-all duration-1000",
                        index === activeImageIndex
                          ? "scale-100 opacity-100"
                          : "scale-[1.025] opacity-0",
                      )}
                    />
                  ),
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-[#071a35]/65 via-transparent to-transparent" />

                <div className="absolute bottom-5 left-5">
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#071a35]/70 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-md">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    Suivi en temps réel
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-center gap-2">
              {JUDO_PRESENCE_PRO.heroImages.map((image, index) => (
                <button
                  key={image.src}
                  type="button"
                  onClick={() => setActiveImageIndex(index)}
                  className={cn(
                    "h-[3px] rounded-full transition-all",
                    index === activeImageIndex
                      ? "w-9 bg-[#ff7900]"
                      : "w-4 bg-white/25 hover:bg-white/50",
                  )}
                  aria-label={`Afficher le visuel ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}